/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
export default {
    baseStyle: {
        container: {
            minWidth: 'xs',
            width: 'full',
            backgroundColor: 'webTheme.layerDefault',
            transition: 'background-color 0.3s ease'
        },
        containerSticky: {
            borderBottom: '1px solid',
            borderBottomColor: 'webBasic.200'
        },
        containerTransparent: {
            backgroundColor: 'transparent',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 'sticky'
        },
        containerTransparentScrolled: {
            backgroundColor: 'rgba(255, 255, 255, 0.70)',
            backdropFilter: 'blur(8px)'
        },
        containerTransparentHovered: {
            backgroundColor: 'white'
        },
        containerSemiTransparentHovered: {
            backgroundColor: 'rgba(255, 255, 255, 0.70)'
        },
        gradientOverlay: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '48px',
            pointerEvents: 'none',
            zIndex: 0,
            transition: 'opacity 0.3s ease'
        },
        content: {
            maxWidth: 'container.xxxl',
            marginLeft: 'auto',
            marginRight: 'auto',
            paddingLeft: [4, 4, 6, 8],
            paddingRight: [4, 4, 6, 8],
            paddingTop: [1, 1, 2, 4],
            paddingBottom: [3, 3, 2, 4],
            position: 'relative',
            zIndex: 1
        },
        headerContent: {
            paddingX: {base: 4, lg: 6},
            paddingY: {base: 2.5, lg: 0},
            height: 12,
            display: {base: 'flex', lg: 'block'}
        },
        mainRow: {
            display: {base: 'flex', lg: 'grid'},
            gridTemplateColumns: {lg: '1fr auto 1fr'},
            wrap: 'nowrap',
            alignItems: 'center',
            justify: 'space-between',
            direction: 'row',
            width: '100%'
        },
        leftSection: {
            alignItems: 'center',
            gap: 2
        },
        centerSection: {
            flex: '1',
            justify: 'center'
        },
        rightSection: {
            alignItems: 'center',
            gap: 2,
            justifySelf: {lg: 'end'}
        },
        searchContainer: {
            order: [2, 2, 2, 'inherit'],
            width: ['full', 'full', 'full', 60],
            marginRight: [0, 0, 0, 4],
            marginBottom: [1, 1, 2, 0]
        },
        bodyContainer: {
            flex: '1'
        },
        logo: {
            width: '109px',
            height: 'auto'
        },
        dynLogo: {
            width: '131px',
            height: 'auto'
        },
        logoButton: {
            variant: 'unstyled',
            display: 'block',
            height: 'auto'
        },
        icons: {
            marginBottom: [1, 1, 2, 0]
        },
        iconButton: {
            width: 6,
            height: 6,
            minWidth: 'unset'
        },
        menuButton: {
            variant: 'unstyled',
            width: 6,
            height: 6,
            minWidth: 'unset'
        },
        searchButtonMobile: {
            variant: 'unstyled',
            width: 6,
            height: 6,
            minWidth: 'unset',
            marginLeft: 2
        },
        searchButtonDesktop: {
            variant: 'ghost',
            display: 'block',
            width: 6,
            height: 6,
            minWidth: 'unset',
            position: 'relative',
            color: 'webButton.btnPrimaryDefault',
            _hover: {
                background: 'unset'
            },
            sx: {
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-3px',
                    left: 0,
                    width: '100%',
                    height: '1px',
                    backgroundColor: 'webTypo.textPrimary',
                    transform: 'scaleX(0)',
                    transformOrigin: 'left center',
                    zIndex: 1001
                },
                '&:hover::before': {
                    transform: 'scaleX(1)',
                    transition: 'transform 0.3s ease'
                }
            }
        },
        wishlistButton: {
            variant: 'unstyled',
            width: 6,
            height: 6,
            minWidth: 'unset',
            display: 'block',
            position: 'relative',
            sx: {
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-3px',
                    left: 0,
                    width: '100%',
                    height: '1px',
                    backgroundColor: 'webTypo.textPrimary',
                    transform: 'scaleX(0)',
                    transformOrigin: 'left center',
                    zIndex: 1001
                },
                '&:hover::before': {
                    transform: 'scaleX(1)',
                    transition: 'transform 0.3s ease'
                }
            }
        },
        accountIcon: {
            variant: 'unstyled',
            height: 6,
            minWidth: 'auto',
            display: 'block',
            cursor: 'pointer',
            position: 'relative',
            alignSelf: ['self-start', 'self-start', 'self-start', 'auto'],
            _focus: {
                boxShadow: 'outline'
            },
            _focusVisible: {
                outline: 0
            },
            sx: {
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-3px',
                    left: 0,
                    width: '100%',
                    height: '1px',
                    backgroundColor: 'webTypo.textPrimary',
                    transform: 'scaleX(0)',
                    transformOrigin: 'left center',
                    zIndex: 1001
                },
                '&:hover::before': {
                    transform: 'scaleX(1)',
                    transition: 'transform 0.3s ease'
                }
            }
        },
        cartButton: {
            variant: 'unstyled',
            display: 'flex',
            height: 6,
            minWidth: 'unset',
            position: 'relative',
            _hover: {
                textDecoration: 'unset'
            },
            sx: {
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-3px',
                    left: 0,
                    width: '100%',
                    height: '1px',
                    backgroundColor: 'webTypo.textPrimary',
                    transform: 'scaleX(0)',
                    transformOrigin: 'left center',
                    zIndex: 1001
                },
                '&:hover::before': {
                    transform: 'scaleX(1)',
                    transition: 'transform 0.3s ease'
                }
            }
        },
        cartCount: {
            marginLeft: 0,
            textStyle: 'bodyXsRegular'
        },
        arrowDown: {
            height: 11,
            minWidth: 'auto',
            marginRight: 0,
            alignSelf: ['self-start', 'self-start', 'self-start', 'auto'],
            cursor: 'pointer',
            _focus: {
                boxShadow: 'outline'
            },
            _focusVisible: {
                outline: 0
            },
            display: ['none', 'none', 'none', 'block']
        },
        wishlistIcon: {
            marginLeft: 2
        },
        signout: {
            width: 'full',
            borderRadius: 'base',
            height: 11,
            padding: 4,
            py: 3,
            marginTop: 1,
            _hover: {
                background: 'webBasic.200'
            }
        },
        signoutIcon: {
            marginRight: 2
        }
    },
    parts: [
        'container',
        'containerSticky',
        'containerTransparent',
        'containerTransparentScrolled',
        'containerTransparentHovered',
        'containerSemiTransparentHovered',
        'gradientOverlay',
        'content',
        'headerContent',
        'mainRow',
        'leftSection',
        'centerSection',
        'rightSection',
        'searchContainer',
        'bodyContainer',
        'logo',
        'dynLogo',
        'logoButton',
        'icons',
        'iconButton',
        'menuButton',
        'searchButtonMobile',
        'searchButtonDesktop',
        'wishlistButton',
        'accountIcon',
        'cartButton',
        'cartCount',
        'arrowDown',
        'wishlistIcon',
        'signout',
        'signoutIcon'
    ]
}
